import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Zap, Users } from 'lucide-react';
import { getLogsAnalytics } from '../../../services/api';

const AnalyticsBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [user, refreshTrigger]);

  const loadAnalytics = async () => {
    if (!user?.organization_id) return;
    
    try {
      const analyticsData = await getLogsAnalytics(user.organization_id);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const getChangeIcon = (change) => {
    return change > 0 ? TrendingUp : TrendingDown;
  };

  const getChangeColor = (change) => {
    return change > 0 ? 'text-celadon-600 dark:text-celadon-400' : 'text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-celadon-600 dark:border-celadon-400"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <BarChart3 className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400">No analytics data available</p>
      </div>
    );
  }

  const stats = [
    {
      id: 'prompts',
      label: 'Total Prompts',
      value: analytics.total_prompts || 0,
      change: analytics.prompts_change || 0,
      icon: BarChart3,
      color: 'blue'
    },
    {
      id: 'executions',
      label: 'Executions',
      value: formatNumber(analytics.total_executions || 0),
      change: analytics.executions_change || 0,
      icon: Zap,
      color: 'green'
    },
    {
      id: 'response-time',
      label: 'Avg Response',
      value: `${analytics.avg_response_time || 0}s`,
      change: analytics.response_time_change || 0,
      icon: TrendingUp,
      color: 'purple'
    },
    {
      id: 'users',
      label: 'Active Users',
      value: analytics.active_users || 0,
      change: analytics.users_change || 0,
      icon: Users,
      color: 'orange'
    }
  ];

  const getDisplayStats = () => {
    switch (size) {
      case 'small':
        return stats.slice(0, 2);
      case 'medium':
        return stats.slice(0, 4);
      case 'large':
        return stats;
      default:
        return stats.slice(0, 4);
    }
  };

  const displayStats = getDisplayStats();

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-celadon-100 text-celadon-600 dark:bg-celadon-900/30 dark:text-celadon-400',
      green: 'bg-celadon-100 text-celadon-600 dark:bg-celadon-900/30 dark:text-celadon-400',
      purple: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400',
      orange: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400'
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="h-full overflow-hidden">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Analytics</span>
      </div>

      <div className={`grid gap-3 h-full ${
        size === 'small' ? 'grid-cols-1' : 
        size === 'large' ? 'grid-cols-2' : 'grid-cols-2'
      }`}>
        {displayStats.map((stat) => {
          const IconComponent = stat.icon;
          const ChangeIcon = getChangeIcon(stat.change);
          
          return (
            <div
              key={stat.id}
              className="p-4 bg-gray-50/80 hover:bg-gray-100/80 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 rounded-2xl border border-gray-100/50 hover:border-gray-200/80 dark:border-gray-600/50 dark:hover:border-gray-500/80 hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.3)] transition-all duration-200 min-h-0 group"
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`p-2 rounded-xl ${getColorClasses(stat.color)} group-hover:scale-105 transition-transform duration-200`}>
                  <IconComponent className="h-4 w-4" />
                </div>
                <div className={`flex items-center text-xs font-medium ${getChangeColor(stat.change)} bg-white/80 dark:bg-gray-800/80 px-2 py-1 rounded-lg`}>
                  <ChangeIcon className="h-3 w-3 mr-1" />
                  {Math.abs(stat.change)}%
                </div>
              </div>
              
              <div className="space-y-1">
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-none">
                  {stat.value}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-tight font-medium">
                  {stat.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {size === 'large' && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-center">
              <p className="font-medium text-gray-900 dark:text-gray-100">{analytics.prompts_this_month || 0}</p>
              <p className="text-gray-600 dark:text-gray-400">This Month</p>
            </div>
            <div className="text-center">
              <p className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(analytics.executions_this_month || 0)}</p>
              <p className="text-gray-600 dark:text-gray-400">Executions</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsBlock; 